import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import c from 'classnames';
import { $getSelection } from 'lexical';
import { up } from 'styled-breakpoints';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import { OptionsBottomDrawer } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { $createTemplateCustomPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-custom-template-placeholder-node';
import { $createTemplatePresetPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-preset-template-placeholder-node';
import { TemplateCustomPlaceholderPopover } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/template-custom-placeholder';
import { TemplatePresetPlaceholderOverlay } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/template-preset-placeholder';
import {
  BasePluginProps,
  PluginIcon,
} from 'src/components/pages/conversations/components/general/lexical-editor/plugins/shared';
import { ContactTextPlaceholderKey } from 'src/components/pages/conversations/components/general/text-placeholders-helpers';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

export const CreateTemplatePlaceholderPlugin = ({
  disabled = false,
}: BasePluginProps) => {
  const { t } = useTranslation(['common', 'conversations']);
  const [editor] = useLexicalComposerContext();
  const insertContactPlaceholderVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));

  const insertPresetPlaceholderVisibilityHandler = useVisible();
  const [selectedPlaceholderKey, setSelectedPlaceholderKey] =
    useState<ContactTextPlaceholderKey | null>();
  const insertCustomPlaceholderVisibilityHandler = useVisible();

  const insertPresetPlaceholder = useCallback(
    (key: ContactTextPlaceholderKey) => {
      setSelectedPlaceholderKey(key);

      insertCustomPlaceholderVisibilityHandler.close();
      insertPresetPlaceholderVisibilityHandler.show();
    },
    [insertCustomPlaceholderVisibilityHandler, insertPresetPlaceholderVisibilityHandler],
  );

  const insertLexicalPresetPlaceholder = (fallbackValue?: string) => {
    if (selectedPlaceholderKey) {
      editor.focus();

      editor.update(() => {
        const selection = $getSelection();

        const templatePlaceholder = $createTemplatePresetPlaceholderNode(
          selectedPlaceholderKey,
          fallbackValue,
        );

        if (selection) {
          selection.insertNodes([templatePlaceholder]);
        }

        insertPresetPlaceholderVisibilityHandler.close();
      });
    }
  };

  const insertCustomPlaceholder = (label: string, description?: string) => {
    editor.focus();

    editor.update(() => {
      const selection = $getSelection();

      const templatePlaceholder = $createTemplateCustomPlaceholderNode(
        label,
        description,
      );

      if (selection) {
        selection.insertNodes([templatePlaceholder]);
      }

      insertPresetPlaceholderVisibilityHandler.close();
    });
  };

  const isOptionActive =
    insertContactPlaceholderVisibilityHandler.visible ||
    insertPresetPlaceholderVisibilityHandler.visible ||
    insertCustomPlaceholderVisibilityHandler.visible;

  const pluginTriggerIcon = useMemo(() => {
    return (
      <PluginIcon
        type="placeholder"
        lggTestId="insert-template-placeholder-plugin-trigger"
        className={c({
          active: isOptionActive,
        })}
        disabled={disabled}
        isOpen={insertContactPlaceholderVisibilityHandler.visible}
        tooltip={t(
          'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.tooltip',
        )}
        onClick={() => {
          insertContactPlaceholderVisibilityHandler.setVisible(
            !insertContactPlaceholderVisibilityHandler.visible,
          );
        }}
      />
    );
  }, [disabled, insertContactPlaceholderVisibilityHandler, isOptionActive, t]);

  const groupedOptions = useMemo(
    () => ({
      [t(
        'conversations:messageInput.options.messageTemplates.createPlaceholderOptions.preset.category',
      )]: [
        {
          label: t('common:firstName'),
          onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.firstName),
          'data-lgg-id': 'placeholder-option-first-name',
        },
        {
          label: t('common:lastName'),
          onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.lastName),
          'data-lgg-id': 'placeholder-option-last-name',
        },
        {
          label: t('common:phoneNumber'),
          onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.phoneNumber),
          'data-lgg-id': 'placeholder-option-phone-number',
        },
        {
          label: t('common:email'),
          onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.email),
          'data-lgg-id': 'placeholder-option-email',
        },
      ],
      [t(
        'conversations:messageInput.options.messageTemplates.createPlaceholderOptions.custom.category',
      )]: [
        {
          label: t(
            'conversations:messageInput.options.messageTemplates.createPlaceholderOptions.custom.options.addCustomPlaceholder',
          ),
          onClick: () => {
            insertCustomPlaceholderVisibilityHandler.show();
            insertPresetPlaceholderVisibilityHandler.close();
          },
          'data-lgg-id': 'placeholder-option-custom',
        },
      ],
    }),
    [
      insertCustomPlaceholderVisibilityHandler,
      insertPresetPlaceholder,
      insertPresetPlaceholderVisibilityHandler,
      t,
    ],
  );

  return (
    <div>
      {breakpointUpMd ? (
        <LggOptionsDropdownButtonWithCustomTrigger
          customTrigger={pluginTriggerIcon}
          customDropdownProps={{
            placement: 'topLeft',
            overlayStyle: {
              height: '254px',
              width: '223px',
            },
          }}
          options={groupedOptions}
          visibilityHandler={insertContactPlaceholderVisibilityHandler}
        />
      ) : (
        <>
          {pluginTriggerIcon}
          <OptionsBottomDrawer
            visible={insertContactPlaceholderVisibilityHandler.visible}
            title={t(
              'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.insertPlaceholder',
            )}
            onClose={insertContactPlaceholderVisibilityHandler.close}
            options={groupedOptions}
          />
        </>
      )}
      <TemplatePresetPlaceholderOverlay
        onSubmit={(fallbackValue) => {
          insertLexicalPresetPlaceholder(fallbackValue);
        }}
        visible={insertPresetPlaceholderVisibilityHandler.visible}
        onClose={insertPresetPlaceholderVisibilityHandler.close}
      />
      <TemplateCustomPlaceholderPopover
        onSubmit={(label, description) => {
          if (label) {
            insertCustomPlaceholder(label, description);
          }
        }}
        visible={insertCustomPlaceholderVisibilityHandler.visible}
        onClose={insertCustomPlaceholderVisibilityHandler.close}
      />
    </div>
  );
};
